import type { JSX } from "react";
import { useRef, useState } from "react";
import { fetchAnswerReasons } from "../../lib/api";
import { readImageFile, type ReadImage } from "../../lib/screenshot";
import {
  IconCopy,
  IconThumbUp,
  IconThumbDown,
  IconRefresh,
  IconHeadset,
  IconPaperclip,
  IconTrash
} from "../icons";

/**
 * Post-answer action row (§2-1): copy, thumbs up/down, regenerate, escalate.
 * Both thumbs open the SAME reason panel: sentiment-aware chips (fast side-model
 * with a fixed fallback), multi-select, plus a free-text "具体说说看" box. Each
 * pill toggle saves immediately and the note saves on Enter/blur — there is no
 * separate submit step — so a rating can carry several reasons + a note with one
 * tap each. See openspec/answer-feedback-multi-reason.md.
 */

interface AnswerActionsProps {
  turnId: string;
  /** The answer text (for copy + reason generation). */
  answer: string;
  /** The user question this answer responded to (for regenerate + reasons). */
  precedingQuestion: string;
  /** Persisted rating, restored on reload (§2-1). */
  initialRating?: ("up" | "down") | undefined;
  /** Persisted picked reasons, restored on reload (§2-1). */
  initialReasons?: string[] | undefined;
  /** Re-ask the preceding question (regenerate). */
  onRegenerate: (question: string) => void;
  /** Escalate this answer to a human. */
  onEscalate: (turnId: string) => void;
  /** Record a thumbs rating (+ optional multi-select reasons / free-text note /
   * screenshot). A `null` rating retracts a prior rating (§3.2). */
  onRate: (
    turnId: string,
    rating: "up" | "down" | null,
    reasons?: string[],
    note?: string,
    screenshot?: { data: string; mimeType: string }
  ) => void;
}

/** Fallback reasons when the side-model is slow/unavailable, per sentiment. */
const FALLBACK_DOWN = ["答案不对", "操作失败", "需要人工", "缺少权限"];
const FALLBACK_UP = ["答案准确", "步骤清晰", "正好需要", "解决了问题"];
const MAX_NOTE_LEN = 200;
const COPIED_RESET_MS = 1600;

export function AnswerActions({
  turnId,
  answer,
  precedingQuestion,
  initialRating,
  initialReasons,
  onRegenerate,
  onEscalate,
  onRate
}: AnswerActionsProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [rating, setRating] = useState<"up" | "down" | null>(initialRating ?? null);
  const restoredReasons = initialReasons ?? [];
  // The panel is open whenever a rating exists (restored or fresh), so the user
  // can add or revise reasons at any time.
  const [open, setOpen] = useState(initialRating !== undefined);
  // On restore, show the matching fixed set immediately (no model call on every
  // load); a fresh thumb fetches sentiment-specific chips.
  const [reasons, setReasons] = useState<string[]>(
    initialRating === "up" ? FALLBACK_UP : initialRating === "down" ? FALLBACK_DOWN : []
  );
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set(restoredReasons));
  const [note, setNote] = useState("");
  // Optional screenshot on thumbs-down (§3.4): collapsed by default, never
  // blocking. Stored as the rich preview shape; only { data, mimeType } is sent.
  const [shot, setShot] = useState<ReadImage | null>(null);
  const [shotError, setShotError] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Whether any reason/note has been saved (drives the subtle acknowledgment).
  const [acked, setAcked] = useState(restoredReasons.length > 0);

  const copy = (): void => {
    void navigator.clipboard
      ?.writeText(answer)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_RESET_MS);
      })
      .catch(() => undefined);
  };

  // Persist the current selection + note. Best-effort; called on every pill
  // toggle and when the note is committed, so there is no separate submit step.
  const persist = (picked: Set<string>, text: string, image: ReadImage | null = shot): void => {
    if (rating === null) return;
    const reasonList = [...picked];
    const trimmed = text.trim();
    onRate(
      turnId,
      rating,
      reasonList.length > 0 ? reasonList : undefined,
      trimmed.length > 0 ? trimmed.slice(0, MAX_NOTE_LEN) : undefined,
      image !== null ? { data: image.data, mimeType: image.mimeType } : undefined
    );
    setAcked(reasonList.length > 0 || trimmed.length > 0 || image !== null);
  };

  // Retract the rating (§3.2): clicking the active thumb again, or the inline
  // "撤回" link, clears the bare rating, collapses the panel, and drops reasons +
  // note so no residual input lingers. `onRate(..., null, ...)` tells the server
  // to clear the persisted rating for this response.
  const clear = (): void => {
    setRating(null);
    setSelected(new Set());
    setNote("");
    setShot(null);
    setShotError(false);
    setAcked(false);
    setOpen(false);
    onRate(turnId, null, [], "");
  };

  // Click a thumb: record the bare rating immediately (the up/down signal must
  // not be lost if the user adds nothing more), open the panel, fetch chips.
  // Re-clicking the already-active thumb is the reverse gesture — retract.
  const thumb = (next: "up" | "down"): void => {
    if (next === rating) {
      clear();
      return;
    }
    setRating(next);
    setSelected(new Set());
    setNote("");
    setShot(null);
    setShotError(false);
    setAcked(false);
    setOpen(true);
    onRate(turnId, next);
    setReasons([]);
    setReasonsLoading(true);
    const fallback = next === "up" ? FALLBACK_UP : FALLBACK_DOWN;
    void fetchAnswerReasons(precedingQuestion, answer, next)
      .then((r) => setReasons(r.length > 0 ? r : fallback))
      .catch(() => setReasons(fallback))
      .finally(() => setReasonsLoading(false));
  };

  // Toggle a pill and save the new selection right away (no submit step).
  const togglePill = (reason: string): void => {
    const nextSet = new Set(selected);
    if (nextSet.has(reason)) nextSet.delete(reason);
    else nextSet.add(reason);
    setSelected(nextSet);
    persist(nextSet, note);
  };

  // Commit the free-text note (on Enter or blur) alongside the current pills.
  const commitNote = (): void => {
    persist(selected, note);
  };

  // Attach an optional screenshot (§3.4): read to base64 and save it onto the
  // same single rating write. Never blocking — a rejected file just shows a hint.
  const pickShot = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;
    const img = await readImageFile(file);
    if (img === null) {
      setShotError(true);
      return;
    }
    setShotError(false);
    setShot(img);
    persist(selected, note, img);
  };

  const removeShot = (): void => {
    setShot(null);
    setShotError(false);
    persist(selected, note, null);
  };

  const promptText =
    rating === "up"
      ? reasonsLoading
        ? "正在分析哪里帮到你…"
        : "方便说下哪里帮到你了吗？"
      : reasonsLoading
        ? "正在分析哪里没解决…"
        : "方便说下是哪里没解决吗？";

  return (
    <div className="answer-actions">
      <div className="answer-actions-row">
        <button
          type="button"
          className={`aa-btn ${copied ? "done" : ""}`}
          aria-label="复制回答"
          title={copied ? "已复制" : "复制"}
          onClick={copy}
        >
          <IconCopy size={15} />
          {copied && <span className="aa-flash">已复制</span>}
        </button>
        <button
          type="button"
          className={`aa-btn up ${rating === "up" ? "active" : ""}`}
          aria-label="有帮助"
          aria-pressed={rating === "up"}
          title="有帮助"
          onClick={() => thumb("up")}
        >
          <IconThumbUp size={15} />
        </button>
        <button
          type="button"
          className={`aa-btn down ${rating === "down" ? "active" : ""}`}
          aria-label="没帮助"
          aria-pressed={rating === "down"}
          title="没帮助"
          onClick={() => thumb("down")}
        >
          <IconThumbDown size={15} />
        </button>
        <button
          type="button"
          className="aa-btn"
          aria-label="重新回答"
          title="重新回答"
          disabled={precedingQuestion.length === 0}
          onClick={() => onRegenerate(precedingQuestion)}
        >
          <IconRefresh size={15} />
        </button>
        <button
          type="button"
          className="aa-btn"
          aria-label="转人工"
          title="转人工"
          onClick={() => onEscalate(turnId)}
        >
          <IconHeadset size={15} />
        </button>
      </div>

      {open && (
        <div className="aa-reasons">
          <span className="aa-reasons-q">
            {promptText}
            {acked && !reasonsLoading && <span className="aa-ack"> · 已记录，谢谢</span>}
            {!reasonsLoading && (
              <button type="button" className="aa-undo" onClick={clear}>
                撤回
              </button>
            )}
          </span>
          {!reasonsLoading && (
            <>
              <div className="aa-reasons-chips">
                {reasons.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className="aa-chip"
                    aria-pressed={selected.has(r)}
                    onClick={() => togglePill(r)}
                  >
                    {r}
                    <svg
                      className="tick"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                      aria-hidden="true"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </button>
                ))}
              </div>
              <div className="aa-reasons-note">
                <input
                  className="aa-other-input"
                  type="text"
                  maxLength={MAX_NOTE_LEN}
                  placeholder="具体说说看（可选，回车保存）"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onBlur={commitNote}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      commitNote();
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                />
              </div>
              <div className="aa-reasons-shot">
                {shot === null ? (
                  <button
                    type="button"
                    className="aa-shot-add"
                    onClick={() => fileRef.current?.click()}
                  >
                    <IconPaperclip size={13} /> 附截图
                  </button>
                ) : (
                  <div className="aa-shot">
                    <img src={shot.dataUrl} alt="截图预览" />
                    <button
                      type="button"
                      className="aa-shot-del"
                      aria-label="移除截图"
                      onClick={removeShot}
                    >
                      <IconTrash size={12} />
                    </button>
                  </div>
                )}
                {shotError && (
                  <span className="aa-shot-error">截图需为 png/jpeg/gif/webp 且小于 3MB</span>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  hidden
                  onChange={(e) => void pickShot(e.target.files?.[0])}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
